import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Stack,
  Title,
  Text,
  Loader,
  Center,
  Button,
  Alert,
  Anchor,
} from '@mantine/core';
import { Download, AlertCircle, CheckCircle, ArrowLeft } from 'react-feather';
import { exportsApi } from '../../services/api/exports';

type PageState = 'loading' | 'downloading' | 'success' | 'error' | 'invalid';

type ErrorCode =
  | 'EXPORT_NOT_FOUND'
  | 'EXPORT_PERMISSION_DENIED'
  | 'EXPORT_NOT_COMPLETED'
  | 'EXPORT_EXPIRED'
  | 'EXPORT_FILE_MISSING'
  | 'UNKNOWN';

interface ErrorCopy {
  title: string;
  body: string;
  hint?: string;
}

const ERROR_COPY: Record<ErrorCode, ErrorCopy> = {
  EXPORT_NOT_FOUND: {
    title: 'Export not found',
    body: 'This export no longer exists. It may have been deleted, or the link in your email may be from a different account.',
    hint: 'Request a fresh export from the page you originally exported from.',
  },
  EXPORT_PERMISSION_DENIED: {
    title: 'You don’t have access to this export',
    body: 'This download link belongs to a different user. Exports can only be downloaded by the person who requested them.',
    hint: 'Sign in with the account that requested this export, or ask that person to re-share the data.',
  },
  EXPORT_NOT_COMPLETED: {
    title: 'Your export is still being prepared',
    body: 'This export hasn’t finished generating yet. You’ll receive a separate “your export is ready” email when it’s available — please use that newer link.',
    hint: 'If it’s been more than a few minutes since you requested it, contact support.',
  },
  EXPORT_EXPIRED: {
    title: 'This download link has expired',
    body: 'Export download links are valid for 24 hours. After that, the file is removed for security.',
    hint: 'Re-request the export from the page you originally exported from.',
  },
  EXPORT_FILE_MISSING: {
    title: 'The exported file is unavailable',
    body: 'The export was marked complete, but the underlying file isn’t reachable right now. This is rare and usually self-corrects.',
    hint: 'Re-request the export. If the problem persists, contact support and include this export’s URL.',
  },
  UNKNOWN: {
    title: 'Download failed',
    body: 'Something went wrong while preparing your download.',
    hint: 'Try refreshing the page, or re-request the export.',
  },
};

function classifyError(err: unknown): { code: ErrorCode; rawMessage: string } {
  if (err instanceof Error) {
    const candidate = (err as Error & { code?: string }).code;
    if (
      candidate === 'EXPORT_NOT_FOUND' ||
      candidate === 'EXPORT_PERMISSION_DENIED' ||
      candidate === 'EXPORT_NOT_COMPLETED' ||
      candidate === 'EXPORT_EXPIRED' ||
      candidate === 'EXPORT_FILE_MISSING'
    ) {
      return { code: candidate, rawMessage: err.message };
    }
    // Backwards-compatible fallbacks for older API responses that only sent strings.
    const msg = err.message.toLowerCase();
    if (msg.includes('not completed')) {
      return { code: 'EXPORT_NOT_COMPLETED', rawMessage: err.message };
    }
    if (msg.includes('expired')) {
      return { code: 'EXPORT_EXPIRED', rawMessage: err.message };
    }
    if (msg.includes('not found')) {
      return { code: 'EXPORT_NOT_FOUND', rawMessage: err.message };
    }
    if (msg.includes('permission')) {
      return { code: 'EXPORT_PERMISSION_DENIED', rawMessage: err.message };
    }
    return { code: 'UNKNOWN', rawMessage: err.message };
  }
  return { code: 'UNKNOWN', rawMessage: 'Failed to download export' };
}

// Trigger a browser download via a transient anchor click. Unlike
// `window.location.href = url`, this hands the URL to the browser's download
// pipeline rather than navigating the current tab — so if the underlying
// CloudFront/S3 response is an error page, the user stays on this page (with
// our error UI) instead of seeing a raw XML body.
function triggerDownload(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
}

export function ExportDownloadPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorCode, setErrorCode] = useState<ErrorCode>('UNKNOWN');
  const [errorRawMessage, setErrorRawMessage] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const triggeredRef = useRef(false);

  const exportId = searchParams.get('id');

  useEffect(() => {
    if (!exportId) {
      setPageState('invalid');
      setErrorRawMessage('No export ID provided');
      return;
    }

    if (triggeredRef.current) return;
    triggeredRef.current = true;

    const fetchAndDownload = async () => {
      try {
        setPageState('downloading');
        const response = await exportsApi.getDownloadUrl(exportId);

        if (!response.download_url) {
          throw new Error('No download URL returned');
        }

        const resolvedFilename = response.filename || 'export.csv';
        setFilename(resolvedFilename);
        setDownloadUrl(response.download_url);
        triggerDownload(response.download_url, resolvedFilename);
        setPageState('success');
      } catch (error) {
        const { code, rawMessage } = classifyError(error);
        setErrorCode(code);
        setErrorRawMessage(rawMessage);
        setPageState('error');
      }
    };

    fetchAndDownload();
  }, [exportId]);

  const renderContent = () => {
    switch (pageState) {
      case 'loading':
      case 'downloading':
        return (
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Title order={3}>Preparing your download...</Title>
            <Text c="dimmed">Please wait while we generate your download link.</Text>
          </Stack>
        );

      case 'success':
        return (
          <Stack align="center" gap="md">
            <CheckCircle size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>Download started</Title>
            <Text c="dimmed">
              Your file <strong>{filename}</strong> should begin downloading automatically.
            </Text>
            {downloadUrl ? (
              <Text size="sm" c="dimmed">
                Didn’t start?{' '}
                <Anchor href={downloadUrl} download={filename} rel="noopener">
                  Click here to download manually.
                </Anchor>
              </Text>
            ) : null}
            <Button
              variant="light"
              leftSection={<ArrowLeft size={16} />}
              onClick={() => navigate('/')}
              mt="md"
            >
              Return to dashboard
            </Button>
          </Stack>
        );

      case 'error': {
        const copy = ERROR_COPY[errorCode];
        return (
          <Stack align="center" gap="md">
            <Alert
              icon={<AlertCircle size={20} />}
              title={copy.title}
              color="red"
              variant="light"
              w="100%"
            >
              <Stack gap="xs">
                <Text>{copy.body}</Text>
                {copy.hint ? (
                  <Text size="sm" c="dimmed">
                    {copy.hint}
                  </Text>
                ) : null}
                {errorRawMessage && errorCode === 'UNKNOWN' ? (
                  <Text size="xs" c="dimmed">
                    Details: {errorRawMessage}
                  </Text>
                ) : null}
              </Stack>
            </Alert>
            <Button
              variant="light"
              leftSection={<ArrowLeft size={16} />}
              onClick={() => navigate('/')}
              mt="md"
            >
              Return to dashboard
            </Button>
          </Stack>
        );
      }

      case 'invalid':
        return (
          <Stack align="center" gap="md">
            <AlertCircle size={48} color="var(--mantine-color-red-6)" />
            <Title order={3}>Invalid request</Title>
            <Text c="dimmed">{errorRawMessage || 'The download link is invalid.'}</Text>
            <Button
              variant="light"
              leftSection={<ArrowLeft size={16} />}
              onClick={() => navigate('/')}
              mt="md"
            >
              Return to dashboard
            </Button>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Container size="sm" py="xl">
      <Card withBorder p="xl" radius="md">
        <Center>
          <Stack align="center" gap="lg" py="xl">
            <Download size={32} color="var(--mantine-color-gray-5)" />
            {renderContent()}
          </Stack>
        </Center>
      </Card>
    </Container>
  );
}

export default ExportDownloadPage;
